import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../api';

export const STOCKS_STALE_TIME = 5 * 60 * 1000;
const STOCKS_CACHE_MAX_AGE = 24 * 60 * 60 * 1000;
const STOCKS_CACHE_KEY = 'insicard:stocks:v1';
const STOCKS_REQUEST_TIMEOUT_MS = 20_000;

export interface Stock {
  [key: string]: any;
  ticker: string;
  indices?: string[];
  General: {
    Name: string;
    LogoURL: string;
    Sector?: string | null;
    NextEarnings?: string | null;
  };
  Highlights: {
    MarketCapitalization: number | null;
    DividendYield: number | null;
    PERatio?: number | null;
    // ... altre proprietà se le usi
  };
  Summary?: {
    FinancialHealth: number;
    ValuationScore: number;
    ProfitabilityScore: number;
    HistoryScore: number;
  };
  AnalystRatings?: {
    TargetPrice?: number | null;
  };
  Statistic?: {
    '1d'?: number;
    '3m'?: number;
    '1y'?: number;
    '5y'?: number;
    '10y'?: number;
  };
  lastPrice?: number | null;
}

interface StocksCacheEntry {
  data: Stock[];
  cachedAt: number;
}

let memoryCache: StocksCacheEntry | null | undefined;

const isStocksData = (value: unknown): value is Stock[] =>
  Array.isArray(value) &&
  value.every(
    stock =>
      stock &&
      typeof stock === 'object' &&
      typeof stock.ticker === 'string' &&
      stock.General &&
      typeof stock.General.Name === 'string'
  );

export const readStocksCache = (): StocksCacheEntry | null => {
  if (memoryCache !== undefined) return memoryCache;
  if (typeof window === 'undefined') return null;

  try {
    const rawCache = window.localStorage.getItem(STOCKS_CACHE_KEY);
    if (!rawCache) {
      memoryCache = null;
      return null;
    }

    const parsed = JSON.parse(rawCache) as Partial<StocksCacheEntry>;
    if (
      typeof parsed.cachedAt !== 'number' ||
      !isStocksData(parsed.data) ||
      Date.now() - parsed.cachedAt > STOCKS_CACHE_MAX_AGE
    ) {
      window.localStorage.removeItem(STOCKS_CACHE_KEY);
      memoryCache = null;
      return null;
    }

    memoryCache = parsed as StocksCacheEntry;
    return memoryCache;
  } catch {
    memoryCache = null;
    return null;
  }
};

const writeStocksCache = (data: Stock[]) => {
  if (typeof window === 'undefined') return;

  // Persist only the fields used by the screener. The API response contains much
  // more detail and can exceed the browser's localStorage quota.
  const compactData = data.map(stock => ({
    ticker: stock.ticker,
    indices: stock.indices,
    General: {
      Name: stock.General.Name,
      LogoURL: stock.General.LogoURL,
    },
    Highlights: {
      MarketCapitalization: stock.Highlights?.MarketCapitalization ?? null,
      DividendYield: stock.Highlights?.DividendYield ?? null,
      PERatio: stock.Highlights?.PERatio ?? null,
    },
    Summary: stock.Summary,
    Statistic: stock.Statistic
      ? {
          '1d': stock.Statistic['1d'],
          '3m': stock.Statistic['3m'],
          '1y': stock.Statistic['1y'],
          '5y': stock.Statistic['5y'],
          '10y': stock.Statistic['10y'],
        }
      : undefined,
  })) as Stock[];

  const entry: StocksCacheEntry = {
    data: compactData,
    cachedAt: Date.now(),
  };
  memoryCache = entry;

  const persist = () => {
    try {
      window.localStorage.setItem(STOCKS_CACHE_KEY, JSON.stringify(entry));
    } catch {
      // The live query remains usable if storage is unavailable or full.
    }
  };

  if (typeof (window as any).requestIdleCallback === 'function') {
    (window as any).requestIdleCallback(persist, { timeout: 2_000 });
  } else {
    window.setTimeout(persist, 0);
  }
};

export const fetchStocksData = async (): Promise<Stock[]> => {
  const response = await api.get('getStocksDashboardData', {
    timeout: STOCKS_REQUEST_TIMEOUT_MS,
  });
  const data = response.data.tickers as unknown;
  if (!isStocksData(data)) {
    throw new Error('The stocks API returned an invalid response.');
  }

  data.sort((a, b) => {
    const mcA = a.Highlights?.MarketCapitalization ?? 0;
    const mcB = b.Highlights?.MarketCapitalization ?? 0;
    return Number(mcB) - Number(mcA);
  });
  writeStocksCache(data);
  return data;
};

export function useStocksData(enabled = true) {
  const cachedStocks = useMemo(readStocksCache, []);

  return useQuery<Stock[], Error>({
    queryKey: ['stocksData'],
    queryFn: fetchStocksData,
    initialData: cachedStocks?.data,
    initialDataUpdatedAt: cachedStocks?.cachedAt,
    placeholderData: previousData => previousData,
    retry: 1,
    retryDelay: failureCount =>
      Math.min(1_000 * Math.pow(2, failureCount), 5_000),
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: true,
    enabled,
    staleTime: STOCKS_STALE_TIME,
    gcTime: STOCKS_CACHE_MAX_AGE,
  });
}
